import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';

import { Close, Glass, Pulldown } from 'jbc-front/components/icons';
import styles from './styles/SelectLeftRightItems.scss';

const Search = ({ onChange, value }) => {
  const { t } = useTranslation();
  return (
    <div className={styles.searchWrap}>
      <input
        type="text"
        className={styles.search}
        placeholder={t('features.ClientCsvFormats.components.SelectGroupItems.input_keyword')}
        value={value}
        onChange={e => onChange(e.target.value)}
      />
      <span className={styles.searchIcon}>
        <Glass size={16} />
      </span>
      {value && (
        <span className={styles.searchClear} onClick={() => onChange('')} role="button" tabIndex="-1">
          <Close size={16} />
        </span>
      )}
    </div>
  );
};

const GroupItem = ({ className, field, onClick }) => (
  <div role="button" tabIndex="-1" className={className} onClick={onClick}>
    {field.label}
  </div>
);

const SelectLeftRightItems = ({ leftItems, rightItems, changeParentState, selectingItems, setSelectingItems }) => {
  const { t } = useTranslation();
  const [searchLeft, setSearchLeft] = useState('');
  const [searchRight, setSearchRight] = useState('');

  const itemsWithSearch = (items = [], searchString = '') =>
    items !== null ? items.filter(item => item.label.indexOf(searchString) >= 0) : null;

  const isSelectedItemLeft = value => rightItems.find(item => item.value === value);

  const addRightItems = () => {
    const newItems = leftItems.filter(item => selectingItems.left.includes(item.value));
    changeParentState(_.uniqBy([...rightItems, ...newItems], item => item.value));
  };

  const isSelectingItems = (fieldItem, position = 'left') => selectingItems[position].some(item => item === fieldItem);

  const unSelectedItem = (fieldItem, position = 'left') =>
    setSelectingItems({ ...selectingItems, [position]: selectingItems[position].filter(item => item !== fieldItem) });

  const selectItem = (fieldItem, position = 'left') =>
    setSelectingItems({ ...selectingItems, [position]: [...selectingItems[position], fieldItem] });

  const selectAllItems = (position = 'left') => {
    const items = position === 'right' ? itemsWithSearch(rightItems, searchRight) : itemsWithSearch(leftItems, searchLeft);
    setSelectingItems({ ...selectingItems, [position]: _.uniq([...items.map(item => item.value)]) });
  };

  const isNoItemsSelected = (position = 'left') => selectingItems[position].length === 0;

  const handleMoveToRight = () => {
    if (isNoItemsSelected()) return;
    addRightItems();
    setSelectingItems({ ...selectingItems, left: [] });
  };

  const handleMoveToLeft = () => {
    if (isNoItemsSelected('right')) return;
    const newItems = rightItems.filter(item => !selectingItems.right.includes(item.value));
    changeParentState(newItems);
    setSelectingItems({ ...selectingItems, right: [] });
  };

  const handleUnSelectAll = (position = 'left') => {
    setSelectingItems({ ...selectingItems, [position]: [] });
  };

  return (
    <div className={styles.multiListContainer}>
      <div style={{ width: '100%' }}>
        <div className={styles.title}>{t('features.ClientMotivation.components.SelectLeftRightItems.motivation_items')}</div>
        <div className={styles.multiList}>
          <Search value={searchLeft} onChange={value => setSearchLeft(value)} />
          <div className={styles.listWithGroup}>
            {itemsWithSearch(leftItems, searchLeft).map(field => {
              let onClick;
              let className = styles.itemDisabled;
              if (!isSelectedItemLeft(field.value)) {
                const selecting = isSelectingItems(field.value);
                if (selecting) {
                  className = styles.itemSelected;
                  onClick = () => unSelectedItem(field.value);
                } else {
                  className = styles.item;
                  onClick = () => selectItem(field.value);
                }
              }
              return <GroupItem key={field.value} className={className} field={field} onClick={onClick} />;
            })}
          </div>
        </div>

        <div className={styles.selectButtons}>
          <a className={styles.selectButton} onClick={() => handleUnSelectAll()} role="button" tabIndex="-1">
            {t('features.ClientCsvFormats.components.SelectGroupItems.deselect')}
          </a>
          <a className={styles.selectButton} onClick={() => selectAllItems()} role="button" tabIndex="-1">
            {t('features.ClientCsvFormats.components.SelectGroupItems.select_all')}
          </a>
        </div>
      </div>
      <div className={styles.buttonsBetween}>
        <a role="button" tabIndex="-1" className={styles.buttonBetween} onClick={handleMoveToRight}>
          <Pulldown size={15} className={styles.selectRight} />
        </a>
        <a role="button" tabIndex="-1" className={styles.buttonBetween} onClick={handleMoveToLeft}>
          <Pulldown size={15} className={styles.selectLeft} />
        </a>
      </div>

      <div style={{ width: '100%' }}>
        <div className={styles.title}>{t('features.ClientMotivation.components.SelectLeftRightItems.active_motivation_items')}</div>
        <div className={styles.multiList}>
          <Search value={searchRight} onChange={value => setSearchRight(value)} />
          <div className={styles.list}>
            {itemsWithSearch(rightItems, searchRight).map(field => {
              const selecting = isSelectingItems(field.value, 'right');
              const className = selecting ? styles.itemSelected : styles.item;
              const onClick = selecting ? () => unSelectedItem(field.value, 'right') : () => selectItem(field.value, 'right');
              return <GroupItem key={field.value} className={className} field={field} onClick={onClick} />;
            })}
          </div>
        </div>

        <div className={styles.selectButtons}>
          <a role="button" tabIndex="-1" className={styles.selectButton} onClick={() => handleUnSelectAll('right')}>
            {t('features.ClientCsvFormats.components.SelectGroupItems.deselect')}
          </a>
          <a role="button" tabIndex="-1" className={styles.selectButton} onClick={() => selectAllItems('right')}>
            {t('features.ClientCsvFormats.components.SelectGroupItems.select_all')}
          </a>
        </div>
      </div>
    </div>
  );
};

export default SelectLeftRightItems;
